import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';
import SEO from '../components/SEO';
import { ArrowRight, Globe, Mic, Target, Mail, Users, Shield, Database, Brain, Zap, BarChart3, Layers, CheckCircle2 } from 'lucide-react';

const PIPELINE = [
  { icon: Globe, title: 'Datenbasis', desc: 'Wir kombinieren öffentliche Firmendaten, Web-Crawls, Branchenverzeichnisse und proprietäre Signale zu einem aktuellen B2B-Datenpool.' },
  { icon: Brain, title: 'KI-Matching', desc: 'Modelle bewerten Passung, Bedarf und Erreichbarkeit jedes Unternehmens zu Ihrem ICP – statt simpler Filterlisten.' },
  { icon: Target, title: 'Hochwertige Leads', desc: 'Sie erhalten qualifizierte Leads mit Score, Begründung, Kontaktdaten und einer fertigen Outreach-Vorlage.' },
];

const SERVICES = [
  { icon: Globe, title: 'Automatische Website- & ICP-Analyse', desc: 'Geben Sie Ihre Domain ein – die KI extrahiert Geschäftsmodell, Produkte und Zielgruppen und erstellt Ihr Ideal-Customer-Profile in Sekunden.' },
  { icon: Mic, title: 'Voice-to-ICP', desc: 'Beschreiben Sie Ihren Wunschkunden frei per Sprache. Unsere KI strukturiert daraus ein präzises Suchprofil – schneller als jedes Formular.' },
  { icon: Database, title: 'Datenbasierte Leadgenerierung', desc: 'Statt klassischer Leadlisten liefern wir kontinuierlich neue, geprüfte B2B-Kontakte – mit Standortverifizierung über Impressums-Abgleich.' },
  { icon: BarChart3, title: 'Matching-Score & Priorisierung', desc: 'Jeder Lead wird auf einer 0–100-Skala bewertet. Ihr Vertrieb arbeitet zuerst die heißesten Kontakte ab – nicht die ersten.' },
  { icon: Mail, title: 'Outreach-Vorbereitung', desc: 'KI-generierte E-Mail-Entwürfe und Telefon-Skripte, individualisiert auf jedes einzelne Unternehmen – einsatzbereit in einem Klick.' },
  { icon: Users, title: 'Team-Kollaboration', desc: 'Bis zu 10 Vertriebler arbeiten gemeinsam an Leads – mit Rollen, Status-Tracking und gemeinsamer Lead-Pipeline.' },
];

const COMPARE = [
  { feature: 'Datenaktualität', laizyleads: 'Wöchentlich aktualisierte Web-Daten', classic: 'Statische Listen, oft Monate alt' },
  { feature: 'Qualität', laizyleads: 'KI-bewertet, hochwertige Leads', classic: 'Massenkontakte, viele Streuverluste' },
  { feature: 'Personalisierung', laizyleads: 'Individuelle Begründung pro Lead', classic: 'Anonyme Adresse + Telefon' },
  { feature: 'Outreach', laizyleads: 'Fertiger Aufhänger pro Unternehmen', classic: 'Selbst recherchieren' },
  { feature: 'Zielgruppen', laizyleads: 'Auch verborgene Nischen', classic: 'Standard-Branchencodes' },
];

export default function LeistungenPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="leistungen-page">
      <SEO
        title="Leistungen – B2B Leadgenerierung Software & Leadgenerator | LaizyLeads"
        description="Leadgenerierung B2B mit KI: Wie der LaizyLeads Leadgenerator gezielt passende Unternehmen findet, qualifizierte Leads liefert und automatisierte, datenbasierte Leadgenerierung möglich macht."
        path="/leistungen"
        keywords="Leadgenerierung Software, B2B Leadgenerierung, Leadgenerator, automatisierte Leadgenerierung, datenbasierte Leadgenerierung, qualifizierte Leads"
      />
      <MarketingNav />

      {/* HERO */}
      <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-gray-50" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <Badge className="mb-6 bg-emerald-100 text-emerald-700 border-emerald-200 px-3.5 py-1.5 text-sm font-medium">
            <Layers className="w-3.5 h-3.5 mr-1.5" /> Leistungen
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
            B2B Leads generieren – datenbasiert,<span className="block text-emerald-600">automatisiert, hochwertig.</span>
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8">
            LaizyLeads ist eine Leadgenerierung-Software, die für Sie täglich neue, passgenaue B2B-Kunden identifiziert. Statt klassischer Adresslisten liefern wir <strong className="text-gray-900">qualifizierte Leads mit Begründung, Score und Kontaktdaten</strong> – fertig für den nächsten Vertriebs-Touchpoint.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white h-12 px-8" onClick={() => navigate('/register')} data-testid="leistungen-register-btn">
              Jetzt kostenlos testen <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50 h-12 px-8" onClick={() => navigate('/kontakt')}>
              Beratung anfragen
            </Button>
          </div>
        </div>
      </section>

      {/* PIPELINE */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-gray-200 text-gray-700 border-gray-300">So funktioniert die Leadgenerierung</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Vom Datenpunkt zum hochwertigen Lead</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Unser Leadgenerator kombiniert Daten, KI-Matching und Vertriebskontext zu einer Pipeline, die hochwertige Leads statt Massenkontakte liefert.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PIPELINE.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="p-7 rounded-2xl border border-gray-200 bg-gray-50">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{p.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 border-emerald-200">Funktionen im Detail</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Alles für effiziente B2B Kundenakquise</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Eine integrierte Plattform für Leadgenerator, Lead-Bewertung und Outreach – ersetzt mehrere Tools auf einmal.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="p-6 rounded-2xl border border-gray-200 bg-white hover:border-emerald-200 hover:shadow-md transition-all">
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-gray-200 text-gray-700 border-gray-300">Abgrenzung</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Leadgenerator vs. klassische Leadlisten</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Bessere Leads statt Massenkontakte – warum eine moderne, automatisierte Leadgenerierung der Adressliste haushoch überlegen ist.
            </p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-5 py-4 font-semibold text-gray-700">Kriterium</th>
                  <th className="text-left px-5 py-4 font-semibold text-emerald-700">LaizyLeads</th>
                  <th className="text-left px-5 py-4 font-semibold text-gray-500">Klassische Listen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {COMPARE.map((c, i) => (
                  <tr key={i}>
                    <td className="px-5 py-4 font-medium text-gray-900">{c.feature}</td>
                    <td className="px-5 py-4 text-emerald-700"><span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {c.laizyleads}</span></td>
                    <td className="px-5 py-4 text-gray-500">{c.classic}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* INTERNAL LINKS / NEXT STEPS */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6">Weiterlesen</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Link to="/branchen" className="group p-6 rounded-2xl border border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all">
              <Zap className="w-5 h-5 text-emerald-600 mb-3" />
              <h3 className="font-semibold mb-1 group-hover:text-emerald-700">Branchen & Use Cases</h3>
              <p className="text-sm text-gray-500">Wie verschiedene Branchen mit unserem Leadgenerator B2B-Neukunden automatisch finden.</p>
            </Link>
            <Link to="/case-studies" className="group p-6 rounded-2xl border border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all">
              <BarChart3 className="w-5 h-5 text-emerald-600 mb-3" />
              <h3 className="font-semibold mb-1 group-hover:text-emerald-700">Case Studies</h3>
              <p className="text-sm text-gray-500">Konkrete Ergebnisse: Mehr Umsatz durch bessere Leads – Storytelling aus Kundenprojekten.</p>
            </Link>
            <Link to="/kontakt" className="group p-6 rounded-2xl border border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md transition-all">
              <Mail className="w-5 h-5 text-emerald-600 mb-3" />
              <h3 className="font-semibold mb-1 group-hover:text-emerald-700">Kontakt</h3>
              <p className="text-sm text-gray-500">Lassen Sie uns über Ihre Leadgenerierung sprechen – kostenloses Beratungsgespräch.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 to-emerald-700" />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">Bereit für hochwertige Leads?</h2>
          <p className="text-emerald-100 text-base md:text-lg mb-8 max-w-xl mx-auto">
            Probieren Sie LaizyLeads aus und finden Sie noch heute Ihre ersten qualifizierten B2B-Leads – ohne Einrichtungsaufwand.
          </p>
          <Button size="lg" className="bg-white hover:bg-gray-100 text-emerald-700 h-12 px-8 text-base shadow-xl font-semibold" onClick={() => navigate('/register')}>
            Jetzt kostenlos testen <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
